import React, { useState, useEffect, useRef, useMemo } from 'react';

interface Category {
  id: number;
  name: string;
  subcategories: Subcategory[];
}

interface Subcategory {
  id: number;
  name: string;
}

interface CategoryOption {
  categoryId: number;
  subcategoryId: number | null;
  categoryName: string;
  subcategoryName: string | null;
  displayText: string;
  searchText: string;
  value: string; // For multi-select (e.g., "cat-123" or "sub-456")
}

interface Props {
  categories: Category[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  className?: string;
}

export const MultiSelectCategoryDropdown: React.FC<Props> = ({
  categories,
  selectedValues,
  onChange,
  placeholder = "Select categories...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [openUpward, setOpenUpward] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Build options list
  const allOptions = useMemo(() => {
    const options: CategoryOption[] = [];

    // Add special "Uncategorized" option
    options.push({
      categoryId: -1,
      subcategoryId: null,
      categoryName: 'Uncategorized',
      subcategoryName: null,
      displayText: 'Uncategorized',
      searchText: 'uncategorized',
      value: 'uncategorized'
    });

    // Add category + subcategory combinations
    categories.forEach(category => {
      // Add category option
      options.push({
        categoryId: category.id,
        subcategoryId: null,
        categoryName: category.name,
        subcategoryName: null,
        displayText: category.name,
        searchText: category.name.toLowerCase(),
        value: `cat-${category.id}`
      });

      // Add subcategory options
      category.subcategories.forEach(subcategory => {
        options.push({
          categoryId: category.id,
          subcategoryId: subcategory.id,
          categoryName: category.name,
          subcategoryName: subcategory.name,
          displayText: `${category.name} - ${subcategory.name}`,
          searchText: `${category.name} ${subcategory.name}`.toLowerCase(),
          value: `sub-${subcategory.id}`
        });
      });
    });

    return options;
  }, [categories]);

  // Filter options based on search term
  const baseFilteredOptions = useMemo(() => {
    if (!searchTerm) return allOptions;
    
    const searchLower = searchTerm.toLowerCase();
    return allOptions.filter(option =>
      option.searchText.includes(searchLower)
    );
  }, [allOptions, searchTerm]);

  // Track the order of options when dropdown opens, and maintain it while open
  // This prevents items from jumping to top when selected
  const [frozenOrder, setFrozenOrder] = useState<CategoryOption[] | null>(null);
  const previousIsOpen = useRef(false);
  const previousSearchTerm = useRef('');

  // Sort options to show selected items at the top when dropdown opens or search changes
  // But maintain order while dropdown is open (so newly selected items don't jump)
  const filteredOptions = useMemo(() => {
    const justOpened = isOpen && !previousIsOpen.current;
    const searchChanged = isOpen && searchTerm !== previousSearchTerm.current;
    
    // When dropdown opens or search changes, sort with selected at top and freeze the order
    if (justOpened || searchChanged) {
      const selected = baseFilteredOptions.filter(opt => selectedValues.includes(opt.value));
      const unselected = baseFilteredOptions.filter(opt => !selectedValues.includes(opt.value));
      const sorted = [...selected, ...unselected];
      setFrozenOrder(sorted);
      previousIsOpen.current = isOpen;
      previousSearchTerm.current = searchTerm;
      return sorted;
    }
    
    // While dropdown is open, maintain frozen order (don't re-sort when items are selected)
    if (isOpen && frozenOrder !== null) {
      // Map frozen order to current filtered options, maintaining positions
      const orderMap = new Map(frozenOrder.map((opt, idx) => [opt.value, idx]));
      const currentInOrder = baseFilteredOptions
        .filter(opt => orderMap.has(opt.value))
        .sort((a, b) => (orderMap.get(a.value) || 0) - (orderMap.get(b.value) || 0));
      
      // Add any new options that weren't in frozen order (shouldn't happen, but safety check)
      const frozenValues = new Set(frozenOrder.map(o => o.value));
      const newOptions = baseFilteredOptions.filter(opt => !frozenValues.has(opt.value));
      
      return [...currentInOrder, ...newOptions];
    }
    
    // When dropdown closes, clear frozen order
    if (!isOpen && previousIsOpen.current) {
      setFrozenOrder(null);
      previousIsOpen.current = false;
      previousSearchTerm.current = '';
    }
    
    // Default: return filtered options with selected at top
    const selected = baseFilteredOptions.filter(opt => selectedValues.includes(opt.value));
    const unselected = baseFilteredOptions.filter(opt => !selectedValues.includes(opt.value));
    return [...selected, ...unselected];
  }, [isOpen, baseFilteredOptions, searchTerm, frozenOrder, selectedValues]);
  
  // Update previousIsOpen ref when isOpen changes (but don't trigger re-sort)
  useEffect(() => {
    previousIsOpen.current = isOpen;
  }, [isOpen]);
  
  // Update previousSearchTerm ref when searchTerm changes (but don't trigger re-sort)
  useEffect(() => {
    previousSearchTerm.current = searchTerm;
  }, [searchTerm]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check dropdown position
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleToggleOption(filteredOptions[highlightedIndex].value);
        }
        break;
    }
  };

  // Get display text for the input
  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return '';
    }

    const selectedOptions = allOptions.filter(option => selectedValues.includes(option.value));
    
    if (selectedValues.length <= 2) {
      return selectedOptions.map(option => option.displayText).join(', ');
    }

    const firstTwo = selectedOptions.slice(0, 2).map(option => option.displayText);
    const remaining = selectedValues.length - 2;
    return `${firstTwo.join(', ')} +${remaining} more`;
  };

  const getPlaceholder = () => {
    return selectedValues.length > 0 ? '' : placeholder;
  };

  return (
    <div className={`multi-select-filter ${className}`} ref={dropdownRef}>
      <div className={`filter-input-container ${isOpen ? 'open' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : getDisplayText()}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setHighlightedIndex(0);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm('');
          }}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholder()}
          className="filter-input"
          autoComplete="off"
        />
        <div 
          className="filter-arrow"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) {
              inputRef.current?.focus();
            }
          }}
        >
          ▼
        </div>
      </div>

      {isOpen && (
        <div 
          className={`filter-dropdown ${openUpward ? 'upward' : ''}`}
          style={{
            maxHeight: '250px',
            overflowY: 'auto'
          }}
        >
          {filteredOptions.length === 0 ? (
            <div className="no-options">No options found</div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                ref={el => optionsRef.current[index] = el}
                className={`filter-option ${highlightedIndex === index ? 'highlighted' : ''} ${selectedValues.includes(option.value) ? 'selected' : ''}`}
                onClick={() => handleToggleOption(option.value)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => {}} // Handled by parent click
                  className="option-checkbox"
                />
                <span className="option-text">{option.displayText}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
